import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import styles from './styles.module.css';
import Loader from '../utils/Loading';
import {
  Button,
  Grid,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { OnlinePurchaseContext } from '../../Context/OnlinePurchaseContext';
import CustomFormSwitch from '../utils/CustomComponents/CustomFormSwitch';
import TerminalServices from '../../services/TerminalServices';
import { REGISTRATION_TYPE, COUNTRY_CONFIGRATION, DEFAULT_COUNTRY, ADDONS } from '../../const';

function RenewPlanPopup({ open, onClose, shop = {} }) {
  const {
    purchaseData,
    setPurchaseData,
    loadSubscriptionsAndAddons,
    isLoading: isLoadingInPurchaseContext,
    subscriptionsAndAddons,
  } = useContext(OnlinePurchaseContext);

  const lastFetchedShopIdRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [yearly, setYearly] = useState(true);
  const [terminals, setTerminals] = useState([]);
  const [currentSub, setCurrentSub] = useState(shop?.subscription || {});
  const [selectedSub, setSelectedSub] = useState(shop?.subscription || {});

  const getRenewPlan = useCallback(() => {
    const { subscription_end_date, subscription, ...shopDetails } = shop;

    const sAndA = subscriptionsAndAddons ?? {};
    const availableAddons = sAndA.add_ons || [];
    const availableSubs = sAndA.subscriptions || [];

    const sub =
      currentSub?.key === selectedSub?.key ? subscription : availableSubs.find((s) => s.key === selectedSub?.key) || {};

    const subscriptionEndDate = new Date(subscription_end_date);
    let amount = yearly ? sub.yearly_price || 0 : sub.monthly_price || 0;

    const subscriptionPlan = {
      key: sub.key,
      id: sub.id,
      type: sub.type,
      validity: yearly ? 365 : 30,
      name: sub.name,
      price: amount,
    };

    let addonsPlan = [];
    if (sub.type === 'premium') {
      addonsPlan.push({
        key: 'expense',
        validity: yearly ? 365 : 30,
        name: 'Expense Support',
        price: 0,
      });
    }

    let freeTerminal = true;
    let addons_with_sub = (sub.config?.addons?.free || []).map((addon) => addon.key);

    addonsPlan = [
      ...addonsPlan,
      ...Object.keys(ADDONS)
        .map((addonKey) => {
          const addon = availableAddons.find((a) => a.key === addonKey);
          if (!addon) return null;

          if (addonKey === 'pos_terminal' || addonKey === 'ecs_terminal') {
            const terminalType = (ADDONS[addonKey] || addonKey.split('_')[0]).toString().toLowerCase();

            const count = terminals.filter((t) => String(t.type).toLowerCase() === terminalType).length;

            if (count <= 0) return null;

            const discountAmount = freeTerminal ? (yearly ? addon.yearly_price : addon.monthly_price) : 0;
            amount -= discountAmount;
            const terminalFree = freeTerminal;
            freeTerminal = false;
            amount += yearly ? addon.yearly_price * count : addon.monthly_price * count;

            return {
              free: terminalFree,
              key: addonKey,
              count,
              name: addon.name,
              price: yearly ? addon.yearly_price : addon.monthly_price,
              validity: yearly ? 365 : 30,
            };
          }

          if (addons_with_sub.includes(addon.key)) return null;

          const shopField = ADDONS[addonKey];
          const validityRaw = shopDetails ? shopDetails[shopField] : undefined;
          const validityDate = validityRaw ? new Date(validityRaw) : NaN;
          if (isNaN(validityDate) || validityDate < subscriptionEndDate) return null;

          const addonAmount = yearly ? addon.yearly_price : addon.monthly_price;
          amount += addonAmount;
          return {
            key: addon.key,
            name: addon.name,
            validity: yearly ? 365 : 30,
            price: addonAmount,
          };
        })
        .filter(Boolean),
    ];

    const tax =
      amount * (COUNTRY_CONFIGRATION[selectedSub?.country]?.tax || COUNTRY_CONFIGRATION[DEFAULT_COUNTRY]?.tax || 0);

    return {
      subscription: subscriptionPlan,
      add_ons: addonsPlan,
      amount,
      tax,
      payable: amount + tax,
      action_type: REGISTRATION_TYPE.RENEWAL,
    };
  }, [shop, terminals, yearly, subscriptionsAndAddons, currentSub?.key, selectedSub?.key, selectedSub?.country]);

  const handleChangeValidity = (event) => {
    setYearly(event.target.checked);
  };

  const getTerminals = useCallback(async (shopId) => {
    if (!shopId) {
      setTerminals([]);
      return;
    }

    if (lastFetchedShopIdRef.current === shopId) return;
    lastFetchedShopIdRef.current = shopId;

    setLoading(true);
    try {
      if (!shopId) {
        console.warn('getTerminals called without shopId');
        setTerminals([]);
        return;
      }
      const resp = await TerminalServices.getTerminals({ shop_id: shopId });

      const termArray = Array.isArray(resp) ? resp : Array.isArray(resp?.data) ? resp.data : [];

      setTerminals(termArray);
    } catch (err) {
      console.error('Failed to fetch terminals', err);
      setTerminals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      const shopId = shop?.id || shop?.shop_id;
      getTerminals(shopId);
      loadSubscriptionsAndAddons();
    }
  }, [open, shop, getTerminals, loadSubscriptionsAndAddons]);

  useEffect(() => {
    if (!shop) return;
    setCurrentSub(shop.subscription);
    setSelectedSub(shop.subscription);
  }, [shop]);

  useEffect(() => {
    if (!subscriptionsAndAddons || !shop) return;
    const plan = getRenewPlan();

    const same =
      purchaseData &&
      JSON.stringify(purchaseData.subscription) === JSON.stringify(plan.subscription) &&
      JSON.stringify(purchaseData.add_ons) === JSON.stringify(plan.add_ons) &&
      purchaseData.amount === plan.amount &&
      purchaseData.tax === plan.tax &&
      purchaseData.payable === plan.payable;

    if (!same) {
      setPurchaseData(plan);
    }
  }, [subscriptionsAndAddons, getRenewPlan, setPurchaseData, shop, purchaseData]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '650px',
          },
        },
      }}
    >
      <DialogTitle>
        Renew Your Plan
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Loader isOpen={loading || isLoadingInPurchaseContext} />

        <Grid display="flex" justifyContent="flex-end" mb={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <span className={styles.text}>Monthly</span>
            <CustomFormSwitch checked={yearly} onChange={handleChangeValidity} />
            <span className={styles.text}>Yearly</span>
          </Stack>
        </Grid>

        {/* Bill Details Section */}
        <Grid container rowGap={2} className={styles.billContainer}>
          <Grid item container rowSpacing={2} className={styles.billWrapper}>
            <Grid item xs={12}>
              <h3>Plan</h3>
            </Grid>
            <Grid item xs={6}>
              <h4>Subscription</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>{purchaseData?.subscription?.name}</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>Validity</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>{purchaseData?.subscription?.validity}</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>Price</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>{purchaseData?.subscription?.price}</h4>
            </Grid>
          </Grid>

          {/* Add-ons */}
          <Grid item container rowSpacing={2} className={styles.billWrapper}>
            <Grid item xs={12}>
              <h3>Add-ons</h3>
            </Grid>
            {purchaseData?.add_ons?.map((adn, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={6}>
                  <h4 style={{ textTransform: 'capitalize' }}>{adn.name}</h4>
                </Grid>
                <Grid item xs={6}>
                  <h4>
                    {adn.price ? adn.price : <span className={styles.hilight}>FREE</span>}
                    {adn.count !== undefined &&
                      ` X ${adn.free ? adn.count - 1 : adn.count} = ${
                        adn.free ? (adn.count - 1) * adn.price : adn.count * adn.price
                      }`}
                    {adn.free && (
                      <span className={styles.hilight}>
                        <Chip label=" 1 FREE" color="success" variant="outlined" className={styles.chip} />
                      </span>
                    )}
                  </h4>
                </Grid>
              </React.Fragment>
            ))}
            {subscriptionsAndAddons?.add_ons
              .filter((a) => (selectedSub.config?.addons?.free || []).some((adn) => adn.key === a.key))
              .map((subAdn) => (
                <>
                  <Grid item xs={6}>
                    <h4 style={{ textTransform: 'capitalize' }}>{subAdn.name}</h4>
                  </Grid>
                  <Grid item xs={6}>
                    <h4>
                      <span className={styles.hilight}>
                        <Chip label="FREE" color="success" variant="outlined" className={styles.chip} />
                      </span>
                    </h4>
                  </Grid>
                </>
              ))}
          </Grid>

          {/* Totals */}
          <Grid item container rowSpacing={2} className={styles.billWrapper}>
            <Grid item xs={6}>
              <h4>Total</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>{purchaseData?.amount || 0}</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>GST (18%)</h4>
            </Grid>
            <Grid item xs={6}>
              <h4>{purchaseData?.tax || 0}</h4>
            </Grid>
            <Grid item xs={6}>
              <h3>Sub Total</h3>
            </Grid>
            <Grid item xs={6}>
              <h3>{purchaseData?.payable || 0}</h3>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export default RenewPlanPopup;
